/**
 * Vocabulário legal do escritório virtual e do ponto.
 * Proibido misturar jornada/expediente com prestador ou função de confiança.
 */

export const ATTENDANCE_POLICIES = Object.freeze({
  CLT_JORNADA: "clt_jornada",
  CLT_CONFIANCA: "clt_confianca",
  ESTAGIO: "estagio",
  PRESTADOR: "prestador",
  OUTRO_CLOCK: "outro_clock",
  OUTRO_NO_CLOCK: "outro_no_clock",
  NEEDS_HR_SETUP: "needs_hr_setup",
});

export function normalizeAttendancePolicy(raw) {
  if (!raw) return null;
  if (typeof raw === "string") {
    const policy = raw;
    return {
      policy,
      clock:
        policy === "clt_jornada" || policy === "estagio" || policy === "outro_clock",
    };
  }
  return raw;
}

export function isClockPolicy(policy) {
  const p = normalizeAttendancePolicy(policy);
  return Boolean(p?.clock);
}

export function isInternshipPolicy(policy) {
  return normalizeAttendancePolicy(policy)?.policy === ATTENDANCE_POLICIES.ESTAGIO;
}

export function isExemptPolicy(policy) {
  const p = normalizeAttendancePolicy(policy);
  if (!p) return true;
  const key = p.policy;
  return (
    key === ATTENDANCE_POLICIES.CLT_CONFIANCA ||
    key === ATTENDANCE_POLICIES.PRESTADOR ||
    key === ATTENDANCE_POLICIES.OUTRO_NO_CLOCK ||
    key === ATTENDANCE_POLICIES.NEEDS_HR_SETUP
  );
}

function isVirtualOfficeFailover(policy) {
  return normalizeAttendancePolicy(policy)?.failover === "virtual_office";
}

const NO_CLOCK_ROOM = {
  joinButton: "Entrar na sala",
  joining: "Entrando…",
  joinHint: "Entrar na sala não registra ponto nem jornada.",
  leaveButton: "Sair da sala",
  leaveTitle: "Sair da sala",
  leaveBody: "Você sai da sala. Isso não registra jornada, ponto nem intervalo.",
  leaveBreak: null,
  leaveExit: "Sair da sala",
  inCallTitle: "Você está na sala",
  endedBody: "Você saiu da sala. Isso não registra jornada.",
  disconnectedBody: "Sua conexão com a sala foi encerrada. Isso não registra jornada.",
  analytics:
    "Minutos neste piso são presença de colaboração e ficam fora de exportes de jornada/AFD.",
};

export function virtualOfficeCopy(policy) {
  const p = normalizeAttendancePolicy(policy);
  const key = p?.policy;
  if (isVirtualOfficeFailover(p)) {
    return {
      ...NO_CLOCK_ROOM,
      subtitle:
        "Sala de colaboração. Sem modalidade cadastrada, o acesso ao escritório virtual usa o regime Outro, sem controle de horário.",
      legalBanner:
        "A modalidade de contratação ainda não foi definida. Como há permissão de escritório virtual, o sistema usa o regime Outro sem ponto — não inventa jornada nem vínculo. O RH pode cadastrar a modalidade depois.",
      inCallBody: "Presença na sala, sem ponto, no regime Outro até o RH concluir o cadastro.",
    };
  }
  if (key === ATTENDANCE_POLICIES.ESTAGIO) {
    return {
      subtitle:
        "Piso da equipe: ao entrar, o horário de estágio é registrado para comprovar o teto da Lei 11.788/2008, art. 10. Estagiário não tem hora extra da CLT; estouro do teto pode descaracterizar o estágio.",
      joinButton: "Ingressar e registrar horário",
      joining: "Ingressando…",
      joinHint: "Ingressar registra o horário de estágio e abre o vídeo.",
      leaveButton: "Sair / Horário",
      leaveTitle: "Registrar saída do estágio",
      leaveBody:
        "Escolha o tipo de saída do horário de estágio. O escritório fecha só para você; os colegas continuam no piso.",
      leaveBreak: "Intervalo",
      leaveExit: "Encerrar horário de estágio",
      legalBanner:
        "Registro para comprovar o limite da Lei 11.788/2008, art. 10. Não é hora extra da CLT. Estouro do teto pode descaracterizar o estágio (art. 3º da mesma lei).",
      inCallTitle: "Você está no escritório",
      inCallBody:
        "Seu horário de estágio está ativo. Câmera e microfone são opcionais.",
      endedBody:
        "O horário de saída do estágio foi registrado. Volte pelo cockpit para um novo ingresso.",
      disconnectedBody:
        "Sua conexão com o escritório foi encerrada. Entre de novo pelo cockpit se ainda estiver no horário de estágio.",
      analytics:
        "Tempo no escritório mede presença no piso. O horário legal de estágio continua em Controle de Ponto.",
    };
  }
  if (key === ATTENDANCE_POLICIES.CLT_CONFIANCA) {
    return {
      subtitle:
        "Piso aberto da equipe. Sua função é de confiança: o tempo aqui é presença colaborativa e não controla horário nem gera extra.",
      joinButton: "Entrar na sala",
      joining: "Entrando…",
      joinHint: "Entrar na sala não registra ponto.",
      leaveButton: "Sair da sala",
      leaveTitle: "Sair do escritório",
      leaveBody:
        "Você sai da sala. Isso não registra jornada, intervalo nem ponto eletrônico.",
      leaveBreak: null,
      leaveExit: "Sair da sala",
      legalBanner:
        "O cadastro indica função de confiança. A isenção de controle de horário, quando cabível, está no CLT art. 62, II e parágrafo único (gestão e gratificação no critério legal). O art. 468 §§ 1º e 2º trata só da reversão da função e da não incorporação da gratificação. Este sistema não registra ponto nem calcula extra. Se houver controle de horário na prática, a isenção pode ser afastada.",
      inCallTitle: "Você está na sala",
      inCallBody:
        "Presença colaborativa, sem controle de horário. Câmera e microfone são opcionais.",
      endedBody: "Você saiu da sala. Isso não registra jornada.",
      disconnectedBody: "Sua conexão com a sala foi encerrada. Isso não registra jornada.",
      analytics:
        "Minutos neste piso são presença de colaboração e ficam fora de exportes de jornada/AFD.",
    };
  }
  if (key === ATTENDANCE_POLICIES.PRESTADOR) {
    return {
      ...NO_CLOCK_ROOM,
      subtitle:
        "Sala de colaboração para a execução do contrato de serviço. Este sistema não registra ponto, não define horário e não cobra presença.",
      legalBanner:
        "Acesso de prestador (PJ ou autônomo) à sala de colaboração do contrato de serviço. Este sistema não registra ponto, não define jornada e não trata presença ou ausência como dever de horário. A natureza do vínculo é a do contrato e da realidade da prestação — o uso desta sala, por si, não caracteriza controle de jornada.",
      inCallBody: "Sala de colaboração. Sair daqui não registra jornada.",
    };
  }
  if (key === ATTENDANCE_POLICIES.OUTRO_NO_CLOCK) {
    return {
      ...NO_CLOCK_ROOM,
      subtitle:
        "Sala de colaboração. O RH definiu este contrato como Outro, sem controle de horário.",
      legalBanner:
        "Regime Outro sem jornada. Este sistema não registra ponto e não trata presença ou ausência como dever de horário.",
      inCallBody: "Sala de colaboração. Sair daqui não registra jornada.",
    };
  }
  if (!key || key === ATTENDANCE_POLICIES.NEEDS_HR_SETUP) {
    return {
      ...NO_CLOCK_ROOM,
      subtitle:
        "Piso da equipe. O RH ainda não definiu sua modalidade de contratação: a presença aqui não registra ponto.",
      joinHint: "Sem modalidade cadastrada, o ingresso não registra ponto.",
      leaveBody: "Você sai da sala. Sem modalidade cadastrada, isso não registra ponto.",
      legalBanner:
        "Peça ao RH para concluir a modalidade de contratação. Enquanto isso, o escritório não registra jornada.",
      inCallBody: "Presença na sala, sem ponto, até o RH concluir o cadastro.",
    };
  }
  if (key === ATTENDANCE_POLICIES.OUTRO_CLOCK) {
    return {
      subtitle:
        "Piso da equipe: ao ingressar, o horário é registrado porque o RH definiu controle para esta modalidade Outro.",
      joinButton: "Ingressar e registrar horário",
      joining: "Ingressando…",
      joinHint: "Ingressar registra o horário e abre o vídeo.",
      leaveButton: "Sair / Horário",
      leaveTitle: "Registrar saída",
      leaveBody:
        "Escolha o tipo de saída. O escritório fecha só para você; os colegas continuam no piso.",
      leaveBreak: "Intervalo",
      leaveExit: "Encerrar horário",
      legalBanner:
        "O RH definiu controle de horário para esta modalidade Outro. As marcações são registro eletrônico interno (Portaria MTP 671/2021 para coletor em software), sem homologação MTP.",
      inCallTitle: "Você está no escritório",
      inCallBody: "Seu horário está ativo. Câmera e microfone são opcionais.",
      endedBody: "O horário de saída foi registrado. Volte pelo cockpit para um novo ingresso.",
      disconnectedBody:
        "Sua conexão com o escritório foi encerrada. Entre de novo pelo cockpit se ainda estiver no horário.",
      analytics:
        "Tempo no escritório mede presença no piso. O horário legal continua em Controle de Ponto.",
    };
  }
  return {
    subtitle:
      "Piso aberto da equipe: ao ingressar, o ponto de entrada é registrado. Use o status para indicar disponibilidade; para falar em privado, chame um colega da lista. Ao sair, escolha intervalo ou fim do expediente.",
    joinButton: "Ingressar e registrar ponto",
    joining: "Ingressando…",
    joinHint: "Ingressar registra o ponto de entrada e abre o vídeo.",
    leaveButton: "Sair / Ponto",
    leaveTitle: "Registrar saída",
    leaveBody:
      "Escolha o tipo de saída para o ponto eletrônico. O escritório fecha só para você; os colegas continuam no piso.",
    leaveBreak: "Intervalo (almoço)",
    leaveExit: "Encerrar expediente",
    legalBanner: null,
    inCallTitle: "Você está no escritório",
    inCallBody:
      "Sua presença e o ponto estão ativos. Colegas entram pelo Escritório Virtual no cockpit — câmera e microfone são opcionais.",
    endedBody:
      "O ponto de saída foi registrado. A sala continua aberta para os colegas. Entre de novo pelo cockpit se ainda estiver no expediente.",
    disconnectedBody:
      "Sua conexão com o escritório virtual foi encerrada. Entre de novo pelo cockpit se ainda estiver no expediente.",
    analytics:
      "Tempo no escritório virtual mede presença no piso (integração de equipes). A jornada legal (CLT / Portaria 671) continua em Controle de Ponto.",
  };
}

/** fetchData relança o body da API no próprio Error (sem axios.response). */
export function readFetchDataError(err) {
  const body = err?.response?.data && typeof err.response.data === "object" ? err.response.data : null;
  const code = body?.code || err?.code || null;
  const data = body?.data || err?.data || {};
  const message = body?.message || err?.message || "";
  return { code, data, message };
}

export function isTimeClockConsentRequiredError(err) {
  const { code, message } = readFetchDataError(err);
  return (
    code === "time_clock.consent_required" ||
    /ciência do controle de horário antes da primeira marcação/i.test(String(message || ""))
  );
}

export function timeClockCopy(policy) {
  const p = normalizeAttendancePolicy(policy);
  const key = p?.policy;
  if (key === ATTENDANCE_POLICIES.ESTAGIO) {
    return {
      titleSelf: "Meu horário de estágio",
      titleOther: "Horário de estágio",
      intro:
        "Registro para comprovar o limite da Lei 11.788/2008, art. 10. Não é hora extra da CLT. Estouro do teto pode descaracterizar o estágio.",
      extraLabel: "Excedente do teto",
      deficitLabel: "Abaixo do previsto",
      lateLabel: "Fora do horário",
      consentButton: "Registrar ciência do horário de estágio",
    };
  }
  if (key === ATTENDANCE_POLICIES.OUTRO_CLOCK) {
    return {
      titleSelf: "Meu controle de horário",
      titleOther: "Controle de horário",
      intro:
        "O RH definiu controle de horário para esta modalidade Outro. Espelho interno em linha com a Portaria MTP 671/2021 (coletor em software — sem homologação MTP).",
      extraLabel: "Excedente",
      deficitLabel: "Déficit",
      lateLabel: "Fora do horário",
      consentButton: "Registrar ciência do horário",
    };
  }
  if (isExemptPolicy(p)) {
    const banner =
      key === ATTENDANCE_POLICIES.CLT_CONFIANCA
        ? "Função de confiança: este sistema não controla horário (CLT art. 62, II e parágrafo único, quando cabível). O art. 468 §§ 1º e 2º regula a reversão da função, não a isenção de ponto."
        : key === ATTENDANCE_POLICIES.PRESTADOR
          ? "Prestador (PJ/autônomo): este sistema não registra ponto nem define jornada. A presença na sala é colaboração do contrato de serviço."
          : key === ATTENDANCE_POLICIES.OUTRO_NO_CLOCK && !isVirtualOfficeFailover(p)
            ? "Regime Outro sem jornada: este sistema não registra ponto nem define horário."
          : isVirtualOfficeFailover(p)
            ? "Modalidade não cadastrada: o acesso ao escritório virtual usa o regime Outro, sem ponto, até o RH definir o contrato. O sistema não inventa jornada nem vínculo."
            : "Sem modalidade de jornada definida. O sistema não registra ponto para não inventar vínculo ou controle.";
    return {
      titleSelf: "Sem controle de jornada",
      titleOther: "Sem controle de jornada",
      intro: banner,
      extraLabel: "Extra",
      deficitLabel: "Déficit",
      lateLabel: "Atraso",
      consentButton: null,
      exempt: true,
    };
  }
  return {
    titleSelf: "Meu ponto eletrônico",
    titleOther: "Ponto eletrônico",
    intro:
      "Espelho de jornada em linha com a Portaria MTP 671/2021 (coletor em software, uso interno — sem homologação MTP). Marcações são imutáveis; correções usam justificativa.",
    extraLabel: "Extra",
    deficitLabel: "Déficit",
    lateLabel: "Atraso",
    consentButton: "Registrar ciência do ponto",
  };
}
